import { useEffect, useState } from "react";
import {
  getTrainingScheduleRecipe,
  isScheduleNotFoundError,
  isScheduleRecipeIncompatibleError,
  type TrainingScheduleRecipeView,
} from "@/api/trainingSchedule";
import { resolveLoadErrorMessage } from "@/components/dataset/loadState";

// 공용 LoadState 와 달리 실패를 두 경우로 나눈다. 복원 불가(409)와 접근 불가(404)는 다시 받아도
// 같은 응답이다. 그래서 다시 시도해도 소용이 없고, 화면은 재시도 버튼을 제거해야 한다.
export type ScheduleRecipeLoadState =
  | { status: "loading" }
  | { status: "success"; data: TrainingScheduleRecipeView }
  | { status: "error"; message: string; retryable: boolean };

/**
 * Schedule recipe 전문을 한 번 불러오는 로더. Recipe 패널이 마운트될 때 받는다.
 *
 * 저장된 recipe 는 Schedule 수명 동안 바뀌지 않으므로 폴링하지 않는다. `reload` 만 다시 받는다.
 */
export function useScheduleRecipe(scheduleId: string): {
  state: ScheduleRecipeLoadState;
  reload: () => void;
} {
  const [state, setState] = useState<ScheduleRecipeLoadState>({ status: "loading" });
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    getTrainingScheduleRecipe(scheduleId).then(
      (data) => {
        if (!cancelled) {
          setState({ status: "success", data });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          setState(errorStateOf(error));
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [scheduleId, reloadTick]);

  return { state, reload: () => setReloadTick((tick) => tick + 1) };
}

// 409 의 원인은 십여 가지로 모이고, 서버는 사유를 영어 원문으로만 준다. 그래서 사용자에게는 무엇이
// 맞지 않았는지를 경우별로 표시한다. 복원 불가는 일부만 채워 표시하지 않고 전체를 실패로 처리한다.
function errorStateOf(error: unknown): ScheduleRecipeLoadState {
  if (isScheduleRecipeIncompatibleError(error)) {
    return {
      status: "error",
      message:
        "저장된 설정을 생성 입력으로 되돌릴 수 없습니다. 지원하지 않는 설정이거나 입력 Dataset·Annotation이 바뀌었을 수 있습니다.",
      retryable: false,
    };
  }
  if (isScheduleNotFoundError(error)) {
    return {
      status: "error",
      message: "Schedule을 찾을 수 없습니다. 삭제되었거나 접근할 수 없습니다.",
      retryable: false,
    };
  }
  return { status: "error", message: resolveLoadErrorMessage(error), retryable: true };
}
